import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Paper
} from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';

// Components
import WorkflowSelector from './components/WorkflowSelector';
import AgentHealthMonitor from './components/AgentHealthMonitor';
import WorkflowController from './components/WorkflowController';
import QuickHelp from './components/QuickHelp';
import RealtimeAgentWorkspace from './components/RealtimeAgentWorkspace';

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Workflow configurations (matching Streamlit dashboard)
export const WORKFLOWS = {
  blog_writer_2: {
    name: "Blog Writer 2 (A2A)",
    description: "4-agent blog creation with manual orchestration",
    icon: "📝",
    status: "available",
    agents: {
      initial_writer: {
        name: "Initial Blog Writer",
        port: 11001,
        url: process.env.REACT_APP_INITIAL_WRITER_URL || "http://localhost:11001",
        description: "Creates comprehensive first drafts",
        icon: "✍️"
      },
      beginner_critic: {
        name: "Beginner Critique Agent", 
        port: 11002,
        url: process.env.REACT_APP_BEGINNER_CRITIC_URL || "http://localhost:11002",
        description: "Reviews for accessibility",
        icon: "🔍"
      },
      expert_critic: {
        name: "Expert Critique Agent",
        port: 11003, 
        url: process.env.REACT_APP_EXPERT_CRITIC_URL || "http://localhost:11003",
        description: "Reviews for technical accuracy",
        icon: "🎯"
      },
      final_writer: {
        name: "Final Writer Agent",
        port: 11004,
        url: process.env.REACT_APP_FINAL_WRITER_URL || "http://localhost:11004", 
        description: "Synthesizes into final post",
        icon: "✨"
      }
    },
    steps: [
      { key: "initial", name: "Initial Draft", agent: "initial_writer", icon: "✍️" },
      { key: "beginner", name: "Beginner Critique", agent: "beginner_critic", icon: "🔍" },
      { key: "expert", name: "Expert Critique", agent: "expert_critic", icon: "🎯" },
      { key: "final", name: "Final Blog", agent: "final_writer", icon: "✨" }
    ]
  },
  lead_magnet_writer: {
    name: "Lead Magnet Writer (A2A)",
    description: "4-agent lead magnet creation from blog feedback",
    icon: "🧲",
    status: "planned",
    agents: {}
  },
  email_sequence: {
    name: "Email Sequence Writer (A2A)", 
    description: "Multi-agent email sequence creation",
    icon: "📧",
    status: "planned",
    agents: {}
  },
  realtime_blog_writer: {
    name: "Real-time Blog Writer (2025)", 
    description: "Modern split-screen UI with live agent streaming",
    icon: "⚡",
    status: "active",
    agents: {},
    realtime: true
  }
};

function App() {
  const [selectedWorkflow, setSelectedWorkflow] = useState('blog_writer_2');
  const [workflowStates, setWorkflowStates] = useState({});

  // Initialize workflow state
  useEffect(() => {
    if (selectedWorkflow && !workflowStates[selectedWorkflow]) {
      if (selectedWorkflow === 'blog_writer_2') {
        setWorkflowStates(prev => ({
          ...prev,
          [selectedWorkflow]: {
            inputText: "",
            results: {},
            stepCompleted: {
              initial: false,
              beginner: false,
              expert: false,
              final: false
            }
          }
        }));
      }
    }
  }, [selectedWorkflow, workflowStates]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ flexGrow: 1 }}>
          {/* App Bar */}
          <AppBar position="static" elevation={1}>
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                🤖 Agent Workflows Central Dashboard
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Central control point for all A2A agent workflows
              </Typography>
            </Toolbar>
          </AppBar>

          {/* Main Content */}
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              {/* Sidebar */}
              <Grid xs={12} md={3}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <WorkflowSelector 
                    workflows={WORKFLOWS}
                    selectedWorkflow={selectedWorkflow}
                    onWorkflowChange={setSelectedWorkflow}
                  />
                </Paper>
                
                <Paper sx={{ p: 2, mb: 2 }}>
                  <AgentHealthMonitor 
                    workflow={WORKFLOWS[selectedWorkflow]}
                  />
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <QuickHelp />
                </Paper>
              </Grid>

              {/* Main Content Area */}
              <Grid xs={12} md={9}>
                {selectedWorkflow === 'realtime_blog_writer' ? (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, background: '#0a0a0a' }}>
                    <RealtimeAgentWorkspace />
                  </div>
                ) : (
                  <WorkflowController
                    workflow={WORKFLOWS[selectedWorkflow]}
                    workflowKey={selectedWorkflow}
                    workflowState={workflowStates[selectedWorkflow]}
                    onStateChange={(newState) => {
                      setWorkflowStates(prev => ({
                        ...prev,
                        [selectedWorkflow]: newState
                      }));
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Container>

          {/* Footer */}
          <Box sx={{ bgcolor: 'background.paper', p: 2, mt: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Agent Workflows Central Dashboard - Manual orchestration of A2A agent workflows
            </Typography>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
